import type { IMetricTier, MetricTierColor } from '@skystone/shared';

// ---------------------------------------------------------------------------
// Colour swatch
// ---------------------------------------------------------------------------

const SWATCH_CLASS: Record<MetricTierColor, string> = {
  success: 'bg-success',
  primary: 'bg-primary',
  warning: 'bg-warning',
  error: 'bg-error',
};

const COLOR_OPTIONS: MetricTierColor[] = ['success', 'primary', 'warning', 'error'];

// ---------------------------------------------------------------------------
// Types for the editor (simplified tier without inclusivity fields)
// ---------------------------------------------------------------------------

export interface EditableTier {
  tierId: string;
  tierOrder: number;
  label: string;
  lowerBound: number | null;
  upperBound: number | null;
  colorToken: MetricTierColor;
  description: string;
}

function toEditableTier(tier: IMetricTier): EditableTier {
  return {
    tierId: tier.tierId,
    tierOrder: tier.tierOrder,
    label: tier.label,
    lowerBound: tier.lowerBound,
    upperBound: tier.upperBound,
    colorToken: tier.colorToken,
    description: tier.description,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TierBoundsEditorProps {
  tiers: IMetricTier[];
  unit: string;
  onChange: (tiers: EditableTier[]) => void;
  validationError?: string | null;
}

export function TierBoundsEditor({ tiers, unit, onChange, validationError }: Readonly<TierBoundsEditorProps>) {
  if (tiers.length === 0) {
    return (
      <div className="rounded-md border border-border bg-muted/30 p-3">
        <p className="text-xs text-muted-foreground">
          This metric does not use tier thresholds. Benchmarks are team-specific — use your own historical average.
        </p>
      </div>
    );
  }

  const editables = tiers.map(toEditableTier);

  function handleFieldChange(index: number, field: keyof EditableTier, value: string) {
    const updated = editables.map((t, i) => {
      if (i !== index) return t;
      if (field === 'lowerBound' || field === 'upperBound') {
        return { ...t, [field]: value === '' ? null : Number(value) };
      }
      return { ...t, [field]: value };
    });
    onChange(updated);
  }

  const sorted = [...editables].sort((a, b) => a.tierOrder - b.tierOrder);

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Tier thresholds ({unit})
      </p>
      {validationError && (
        <p className="text-xs text-error" role="alert">{validationError}</p>
      )}
      <div className="overflow-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="pb-1 pr-2 font-medium text-muted-foreground">Colour</th>
              <th className="pb-1 pr-2 font-medium text-muted-foreground">Label</th>
              <th className="pb-1 pr-2 font-medium text-muted-foreground">Lower</th>
              <th className="pb-1 pr-2 font-medium text-muted-foreground">Upper</th>
              <th className="pb-1 font-medium text-muted-foreground">Description</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((tier, idx) => {
              const origIdx = editables.findIndex((t) => t.tierId === tier.tierId);
              return (
                <tr key={tier.tierId} className="border-b border-border/50">
                  <td className="py-1 pr-2">
                    <div className="flex items-center gap-1">
                      <span
                        className={`inline-block h-3 w-3 rounded-full ${SWATCH_CLASS[tier.colorToken]}`}
                        aria-label={`Colour: ${tier.colorToken}`}
                      />
                      <select
                        className="rounded border border-border bg-background px-1 py-0.5 text-xs"
                        value={tier.colorToken}
                        onChange={(e) => handleFieldChange(origIdx, 'colorToken', e.target.value)}
                        aria-label={`Colour for ${tier.label}`}
                      >
                        {COLOR_OPTIONS.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      className="w-full rounded border border-border bg-background px-1 py-0.5 text-xs"
                      value={tier.label}
                      onChange={(e) => handleFieldChange(origIdx, 'label', e.target.value)}
                      aria-label={`Label for tier ${idx + 1}`}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      type="number"
                      step="any"
                      className="w-20 rounded border border-border bg-background px-1 py-0.5 text-xs"
                      value={tier.lowerBound ?? ''}
                      placeholder="−∞"
                      onChange={(e) => handleFieldChange(origIdx, 'lowerBound', e.target.value)}
                      aria-label={`Lower bound for ${tier.label}`}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      type="number"
                      step="any"
                      className="w-20 rounded border border-border bg-background px-1 py-0.5 text-xs"
                      value={tier.upperBound ?? ''}
                      placeholder="+∞"
                      onChange={(e) => handleFieldChange(origIdx, 'upperBound', e.target.value)}
                      aria-label={`Upper bound for ${tier.label}`}
                    />
                  </td>
                  <td className="py-1">
                    <input
                      className="w-full rounded border border-border bg-background px-1 py-0.5 text-xs"
                      value={tier.description}
                      onChange={(e) => handleFieldChange(origIdx, 'description', e.target.value)}
                      aria-label={`Description for ${tier.label}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { toEditableTier };
